import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../UserContext';
import {
  NOTES_MAX_LENGTH,
  NotesConflictError,
  loadEvalNotes,
  normalizeNotes,
  saveEvalNotes,
} from './cloudEvalNotes';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatTimestamp(value) {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

const STATUS_TONES = {
  error: 'text-red-700',
  ok: 'text-green-700',
  muted: 'text-gray-500',
};

/**
 * Collapsible shell. The status line lives in the <summary> so saving/error
 * states stay visible (and are announced) while the panel is collapsed.
 */
function NotesShell({ headingId, defaultOpen = false, dirty = false, status, statusId, children }) {
  return (
    <details className="group mb-6 rounded-md border border-gray-200 bg-white" open={defaultOpen || undefined}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-4 py-3">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-900" id={headingId}>Internal notes</span>
          {dirty && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Unsaved changes
            </span>
          )}
          <span
            className={classNames('text-sm', STATUS_TONES[status?.tone ?? 'muted'])}
            id={statusId}
            role="status"
          >
            {status?.text ?? ''}
          </span>
        </span>
        <span className="text-sm text-indigo-600 group-open:hidden">Click to expand</span>
        <span className="hidden text-sm text-indigo-600 group-open:inline">Click to collapse</span>
      </summary>
      <div className="space-y-3 border-t border-gray-200 px-4 py-4">
        {children}
      </div>
    </details>
  );
}

function NotesEditor({ evaluationId, loaded, onReload }) {
  const user = useContext(UserContext);
  const mountedRef = useRef(true);
  const savingRef = useRef(false);
  const [saved, setSaved] = useState(loaded);
  const [draft, setDraft] = useState(loaded.notes);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [defaultOpen] = useState(normalizeNotes(loaded.notes) !== '');

  const dirty = normalizeNotes(draft) !== normalizeNotes(saved.notes);
  const saving = status === 'saving';
  const fieldId = `eval-notes-${evaluationId}`;
  const headingId = `${fieldId}-heading`;
  const hintId = `${fieldId}-hint`;
  const statusId = `${fieldId}-status`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    function warnUnsaved(event) {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', warnUnsaved);
    return () => window.removeEventListener('beforeunload', warnUnsaved);
  }, [dirty]);

  function handleChange(event) {
    setDraft(event.target.value);
    // Keep an error visible until the next save attempt; clear other states.
    if (!saving && status !== 'error') setStatus('idle');
  }

  async function handleSave() {
    if (!dirty || savingRef.current) return;
    savingRef.current = true;
    setStatus('saving');
    setErrorMessage(null);
    setConflict(false);
    try {
      const result = await saveEvalNotes({
        evaluationId,
        notes: draft,
        userId: user?.uid,
        expectedUpdatedAt: saved.notesUpdatedAt,
      });
      // The reviewer may have switched evals mid-save; never touch another panel.
      if (!mountedRef.current) return;
      setSaved(result);
      setUnconfirmed(!result.confirmed);
      setStatus('saved');
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Failed to save evaluation notes:', err);
      setConflict(err instanceof NotesConflictError);
      setErrorMessage(err?.message || 'Unknown error');
      setStatus('error');
    } finally {
      savingRef.current = false;
    }
  }

  const lastSaved = formatTimestamp(saved.notesUpdatedAt);
  let shellStatus = null;
  if (status === 'saving') {
    shellStatus = { tone: 'muted', text: 'Saving notes…' };
  } else if (status === 'saved') {
    shellStatus = unconfirmed
      ? { tone: 'ok', text: 'Notes saved. Reload before editing again.' }
      : { tone: 'ok', text: 'Notes saved' };
  } else if (status === 'error') {
    shellStatus = { tone: 'error', text: conflict ? errorMessage : `Failed to save notes: ${errorMessage}` };
  } else if (lastSaved) {
    shellStatus = { tone: 'muted', text: `Last saved ${lastSaved}` };
  }

  return (
    <NotesShell
      defaultOpen={defaultOpen}
      dirty={dirty}
      headingId={headingId}
      status={shellStatus}
      statusId={statusId}
    >
      <p className="text-sm text-gray-600" id={hintId}>
        Internal — never included in the report or PDF.
      </p>
      <textarea
        aria-describedby={status === 'error' ? `${hintId} ${statusId}` : hintId}
        aria-invalid={status === 'error' || undefined}
        aria-labelledby={headingId}
        className={classNames(
          'block w-full rounded-md border-0 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-gray-50',
          status === 'error' ? 'ring-red-400' : 'ring-gray-300'
        )}
        disabled={saving}
        id={fieldId}
        maxLength={NOTES_MAX_LENGTH}
        onChange={handleChange}
        placeholder="Add notes about this evaluation…"
        rows={5}
        value={draft}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!dirty || saving}
          onClick={handleSave}
          type="button"
        >
          {saving ? 'Saving…' : 'Save notes'}
        </button>
        {dirty && !saving && (
          <button
            className="text-sm font-medium text-gray-600 hover:text-gray-800"
            onClick={() => {
              setDraft(saved.notes);
              setStatus('idle');
            }}
            type="button"
          >
            Discard changes
          </button>
        )}
        {(conflict || unconfirmed) && !saving && (
          <button
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            onClick={onReload}
            type="button"
          >
            Reload notes
          </button>
        )}
      </div>
    </NotesShell>
  );
}

/**
 * Internal reviewer notes for a cloud eval, loaded from cloud_eval_notes.
 * Rendered above the tab content so it stays mounted (and keeps unsaved
 * edits) while the reviewer switches tabs. Callers key it on the evaluation
 * id; the editor is also keyed per load, so a reload or a return to this eval
 * always starts from the freshly loaded doc.
 */
export default function EvalNotesPanel({ evaluationId }) {
  const [loadCount, setLoadCount] = useState(0);
  const [state, setState] = useState({ loadKey: null, status: 'loading', data: null, error: null });
  const loadKey = `${evaluationId}:${loadCount}`;

  useEffect(() => {
    let active = true;
    const key = `${evaluationId}:${loadCount}`;
    loadEvalNotes({ evaluationId })
      .then((data) => {
        if (active) setState({ loadKey: key, status: 'ready', data, error: null });
      })
      .catch((err) => {
        console.error('Failed to load evaluation notes:', err);
        if (active) setState({ loadKey: key, status: 'error', data: null, error: err?.message || 'Unknown error' });
      });
    return () => {
      active = false;
    };
  }, [evaluationId, loadCount]);

  const reload = useCallback(() => setLoadCount((count) => count + 1), []);
  const fieldId = `eval-notes-${evaluationId}`;

  // Only trust a result that belongs to the current evaluation + load.
  const current = state.loadKey === loadKey ? state : { status: 'loading' };

  if (current.status === 'ready') {
    return (
      <NotesEditor
        evaluationId={evaluationId}
        key={loadKey}
        loaded={current.data}
        onReload={reload}
      />
    );
  }

  const loadFailed = current.status === 'error';
  return (
    <NotesShell
      headingId={`${fieldId}-heading`}
      status={loadFailed
        ? { tone: 'error', text: `Failed to load notes: ${current.error}` }
        : { tone: 'muted', text: 'Loading notes…' }}
      statusId={`${fieldId}-status`}
    >
      {loadFailed && (
        <button
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          onClick={reload}
          type="button"
        >
          Retry
        </button>
      )}
    </NotesShell>
  );
}
